import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Truck, AlertTriangle, ClipboardCheck, Ban, MinusCircle, ArrowLeft, Send, Clock } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

// "Order stock now" ORDER ROUND (ordernow1021a2, Paul 21 Oct 2026). Read-only here: per supplier the PO, its lines
// (item, code, pieces, "3 boxes of 6 = 18", price, line total, every flag), the supplier total ex VAT; then Needs
// checking (one-step confirm from the box / shelf photo), the excluded list and the left-out list. An OPEN-RETURN line
// takes a "keep, because ..." reason. Phone width (390px) first.
// ASK FOR SPECIALS (ordernow1021b1): each supplier card shows the exact price-enquiry text and where it goes (the
// supplier's VERIFIED contact; nobody types an address). Staff check it and tap Send ONCE (the server makes a second tap
// a no-op). Kill switch SUPPLIER_SENDS_ENABLED OFF: "Sends are switched off: saved, not sent". A message stuck in
// sending (no answer from the 071 sender for 10+ minutes) is listed for a human to mark sent or failed, with a note.
// SPECIALS 2 of 2 (ordernow1021b2): each supplier card shows the 1-working-day wait ("No reply: order at last price"
// once it is over; "Skip the wait" needs a written reason), "Record the reply" (pasted text, photos resized on the
// tablet, per line the price per box now, a special or "No specials"), and on each line the decision: special taken /
// declined and why. A declined special can be taken anyway with a written reason (shown to the approver), never one
// declined by the R5,000 cap. Nothing is sent from here.
// CHASING (ordernow1021d2): each card shows the chase draft (short, back-order, 5 working days with no delivery, past
// the expected date) with a Send tap, and the price differences / credit claims: those wait for Paul ("For Paul" list,
// owner only) and get a Send only once he approved raising one.

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

async function sendJson(url, method, body) {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  })
  let data = {}
  try { data = await res.json() } catch (_) { data = {} }
  return { ok: res.ok && data.ok !== false, data }
}

const INPUT = 'w-full rounded-xl border border-brand-border bg-white px-3 py-2 text-base text-brand-ink focus:border-brand-primary focus:outline-none'

const FLAG_LABELS = {
  expiring_soon: 'Expiring soon',
  counter_changed: 'Counter changed the amount',
  on_order: 'Some already on order',
  open_return: 'Open return',
  special_override: 'Special taken by override',
  usage_from_purchases: 'Usage from purchases',
  no_usage_data: 'No usage data',
  over_3_months: 'Over 3 months of cover',
}
const ENQUIRY_POLL_MS = 20000
const WARN_FLAGS = new Set(['open_return', 'over_3_months', 'expiring_soon', 'counter_changed', 'special_override'])

function rands(v) {
  if (v === null || v === undefined || v === '') return '-'
  const n = Number(v)
  return Number.isFinite(n) ? `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : String(v)
}

function packText(l) {
  const box = l.packs === 1 ? 'box' : 'boxes'
  return `${l.packs} ${box} of ${l.pack_size_used} = ${l.pieces_ordered}`
}

function when(iso) {
  return iso ? String(iso).slice(0, 16).replace('T', ' ') : ''
}

export default function OrderRound({ take, blocker, orders = [], needs_checking = [], excluded = [], left_out = [], total_ex_vat, suppliers = [], default_name = '' }) {
  const [name, setName] = useState(default_name || '')
  const [enq, setEnq] = useState(null)
  const [rep, setRep] = useState(null)
  const started = !!take.order_round_started_at
  const loadEnquiries = useCallback(async () => {
    try {
      const res = await fetch(`/stock_takes/${take.id}/enquiries`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.ok) setEnq(await res.json())
    } catch (_) { /* the cards still show; the enquiry panel stays empty */ }
  }, [take.id])
  useEffect(() => { if (orders.length) loadEnquiries() }, [orders.length, loadEnquiries])
  const loadReplies = useCallback(async () => {
    try {
      const res = await fetch(`/stock_takes/${take.id}/replies`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.ok) setRep(await res.json())
    } catch (_) { /* the cards still show; the reply panel stays empty */ }
  }, [take.id])
  useEffect(() => { if (orders.length) loadReplies() }, [orders.length, loadReplies])
  const [appr, setAppr] = useState(null)
  const loadApprovals = useCallback(async () => {
    try {
      const res = await fetch(`/stock_takes/${take.id}/approvals`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.ok) setAppr(await res.json())
    } catch (_) { /* the cards still show; the approval panel stays empty */ }
  }, [take.id])
  useEffect(() => { if (orders.length) loadApprovals() }, [orders.length, loadApprovals])
  // An approval / void / send / placement changes statuses and the order text: reload the page props and all panels.
  const apprFor = (id) => (appr && appr.orders ? appr.orders.find((x) => x.order_id === id) : null)
  // RECEIVE + INVOICES (ordernow1021d1): deliveries, the 3-way match, invoices and portal orders per order.
  const [recv, setRecv] = useState(null)
  const loadReceiving = useCallback(async () => {
    try {
      const res = await fetch(`/stock_takes/${take.id}/receiving`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.ok) setRecv(await res.json())
    } catch (_) { /* the cards still show; the receiving panel stays empty */ }
  }, [take.id])
  // Fix M4: loaded even when this count has no orders (Unmatched invoices / portal orders can still be listed here).
  useEffect(() => { loadReceiving() }, [loadReceiving])
  // Fix M3: an approval / void / send / placement also refreshes the receiving panel.
  const approvalsChanged = useCallback(() => { loadApprovals(); loadEnquiries(); loadReplies(); loadReceiving(); router.reload({ preserveScroll: true }) }, [loadApprovals, loadEnquiries, loadReplies, loadReceiving])
  const receivingChanged = useCallback(() => { loadReceiving(); loadApprovals(); router.reload({ preserveScroll: true }) }, [loadReceiving, loadApprovals])
  const recvFor = (id) => (recv && recv.orders ? recv.orders.find((x) => x.order_id === id) : null)
  // A placed portal order shows 'not picked up yet' 1 hour after placing: refresh at that moment (fix C6).
  useEffect(() => {
    const due = (appr && appr.orders ? appr.orders : []).map((x) => (x.portal && x.portal.notice_at && !x.portal.notice ? Date.parse(x.portal.notice_at) : NaN))
      .filter((t) => Number.isFinite(t) && t > Date.now())
    if (!due.length) return undefined
    const t = setTimeout(loadApprovals, Math.min(Math.min(...due) - Date.now() + 2000, 2147483000))
    return () => clearTimeout(t)
  }, [appr, loadApprovals])
  // A reply / override / skip changes the lines, prices and totals: reload the page props and the reply panel.
  const repliesChanged = useCallback(() => { loadReplies(); loadApprovals(); router.reload({ preserveScroll: true }) }, [loadReplies, loadApprovals])
  const repFor = (id) => (rep && rep.orders ? rep.orders.find((x) => x.order_id === id) : null)
  // Keep statuses fresh: refresh when the tablet comes back to this tab, and every 20 s while anything is queued or
  // sending (so "Queued" turns into sent / failed / STUCK without anyone navigating).
  const inFlightMsgs = !!(enq && ((enq.stuck || []).length > 0 || (enq.orders || []).some((x) =>
    (x.messages || []).some((m) => m.status === 'queued' || m.status === 'sending'))))
  useEffect(() => {
    if (!orders.length) return undefined
    const onFocus = () => { loadEnquiries(); loadApprovals() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [orders.length, loadEnquiries, loadApprovals])
  useEffect(() => {
    if (!inFlightMsgs) return undefined
    const t = setInterval(loadEnquiries, ENQUIRY_POLL_MS)
    return () => clearInterval(t)
  }, [inFlightMsgs, loadEnquiries])
  // The order message's status (queued -> sent = ordered) refreshes on the same 20 s beat.
  useEffect(() => {
    if (!inFlightMsgs && !(appr && (appr.orders || []).some((x) => (x.messages || []).some((m) => m.status === 'queued' || m.status === 'sending')))) return undefined
    const t = setInterval(loadApprovals, ENQUIRY_POLL_MS)
    return () => clearInterval(t)
  }, [inFlightMsgs, appr, loadApprovals])
  const enqFor = (id) => (enq && enq.orders ? enq.orders.find((x) => x.order_id === id) : null)
  // CHASING (ordernow1021d2): chase drafts + Paul-approved differences per order.
  const [chs, setChs] = useState(null)
  const loadChases = useCallback(async () => {
    try {
      const res = await fetch(`/stock_takes/${take.id}/chases`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      if (res.ok) setChs(await res.json())
    } catch (_) { /* the cards still show; the chase panel stays empty */ }
  }, [take.id])
  useEffect(() => { if (orders.length) loadChases() }, [orders.length, loadChases])
  const chsFor = (id) => (chs && chs.orders ? chs.orders.find((x) => x.order_id === id) : null)
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
        <Link href={`/stock_takes/${take.id}`} className="mb-3 inline-flex items-center gap-1 text-sm text-brand-muted"><ArrowLeft size={14} /> Stock take #{take.id}</Link>
        <h1 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-brand-ink">
          <Truck size={18} /> Order round · Stock take #{take.id}
          {take.scoped && <span className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-semibold text-brand-primary">{take.scope_label}</span>}
        </h1>
        <p className="mt-1 text-xs text-brand-muted">
          {started ? `Started by ${take.order_round_started_by} at ${when(take.order_round_started_at)}` : (blocker || 'Not started yet')}
          {' · '}Prices per pack, ex VAT. A message leaves only after a Send tap.
        </p>
        <label className="mt-3 block text-xs font-medium text-brand-muted">Your name (stamped on what you save here)
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={cn(INPUT, 'mt-1 sm:max-w-xs')} />
        </label>

        {orders.length > 0 && (
          <p className="mt-4 text-sm font-semibold text-brand-ink">{orders.length} supplier {orders.length === 1 ? 'order' : 'orders'} · total {rands(total_ex_vat)} ex VAT</p>
        )}
        {enq && !enq.sends_enabled && orders.length > 0 && (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">Sends are switched off: a tapped message is saved, not sent.</p>
        )}
        {enq && enq.stuck && enq.stuck.length > 0 && <StuckList rows={enq.stuck} name={name} onDone={loadEnquiries} />}
        {appr && !appr.pin_configured && orders.length > 0 && (
          <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-900">{appr.pin_missing_text}</p>
        )}
        {orders.map((o) => <OrderCard key={o.id} order={o} name={name} enquiry={enqFor(o.id)} sendsEnabled={!!(enq && enq.sends_enabled)} onSent={loadEnquiries}
          reply={repFor(o.id)} onReplyChanged={repliesChanged} approval={apprFor(o.id)} onApprovalChanged={approvalsChanged}
          receiving={recvFor(o.id)} recvMeta={recv} onReceivingChanged={receivingChanged}
          chase={chsFor(o.id)} chaseCap={chs ? chs.wa_cap : null} onChaseSent={loadChases} />)}
        {recv && <UnmatchedSection takeId={take.id} recv={recv} name={name} onChanged={receivingChanged} />}

        <ListSection icon={<ClipboardCheck size={16} />} title={`Needs checking (${needs_checking.length})`}
          note="Confirm these from the box or the shelf photo before they can be ordered.">
          {needs_checking.map((r) => <NeedsCheckingRow key={r.stock_item_id} row={r} suppliers={suppliers} name={name} />)}
        </ListSection>
        <ListSection icon={<Ban size={16} />} title={`Excluded (${excluded.length})`} note="excluded: order outside, then log it">
          {excluded.map((r) => (
            <div key={r.stock_item_id} className="px-4 py-3 text-sm">
              <p className="font-medium text-brand-ink">{r.name}</p>
              <p className="text-xs text-brand-muted">{r.supplier_name} · {r.pieces_needed} pieces · {r.reason}</p>
            </div>
          ))}
        </ListSection>
        <ListSection icon={<MinusCircle size={16} />} title={`Left out (${left_out.length})`}>
          {left_out.map((r) => (
            <div key={r.stock_item_id} className="px-4 py-3 text-sm">
              <p className="font-medium text-brand-ink">{r.name}</p>
              <p className="text-xs text-brand-muted">{r.reason}</p>
            </div>
          ))}
        </ListSection>
      </div>
    </DashboardLayout>
  )
}

function OrderCard({ order, name, enquiry, sendsEnabled, onSent, reply, onReplyChanged, approval, onApprovalChanged, receiving, recvMeta, onReceivingChanged, chase, chaseCap, onChaseSent }) {
  const showReceiving = !!(receiving && (receiving.receivable || receiving.deliveries.length || receiving.invoices.length || receiving.portal_orders.length))
  const decFor = (id) => (reply && reply.lines ? reply.lines.find((x) => x.id === id) : null)
  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-brand-border bg-white">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-brand-border bg-brand-surface px-4 py-3">
        <div className="min-w-0">
          <p className="font-semibold text-brand-ink">{order.supplier_name}</p>
          <p className="text-xs text-brand-muted">PO {order.po_number} · {order.status.replace(/_/g, ' ')} · {order.channel}
            {!order.contact_verified && order.channel !== 'portal' ? ' · no verified contact' : ''}</p>
        </div>
        <p className="text-sm font-semibold text-brand-ink">{rands(order.total_ex_vat)} <span className="text-xs font-normal text-brand-muted">ex VAT</span></p>
      </div>
      <div className="divide-y divide-brand-border">
        {order.lines.map((l) => <LineRow key={l.id} line={l} editable={order.status === 'planning'} name={name} special={decFor(l.id)} onSpecialChanged={onReplyChanged} />)}
      </div>
      {enquiry && <EnquiryPanel e={enquiry} name={name} sendsEnabled={sendsEnabled} onSent={onSent} />}
      {reply && <SpecialsPanel r={reply} name={name} onChanged={onReplyChanged} />}
      {approval && <ApprovalPanel a={approval} name={name} sendsEnabled={sendsEnabled} onChanged={onApprovalChanged} />}
      {showReceiving && <ReceivingPanel r={receiving} name={name} pinConfigured={!!(recvMeta && recvMeta.pin_configured)}
        reasons={recvMeta ? recvMeta.return_reasons : []} onChanged={onReceivingChanged} />}
      {chase && <ChasePanel c={chase} cap={chaseCap} name={name} sendsEnabled={sendsEnabled} onSent={onChaseSent} />}
    </div>
  )
}

// CHASING (ordernow1021d2): a short line, a back-order, no delivery 5 working days after ordering, or past the expected
// date -> Ivory drafts the chase; staff check it and tap Send ONCE (same outbox, dedupe and kill switch as the enquiry).
// A price difference / credit claim has NO Send here: it waits on Paul's "For Paul" list; only one he APPROVED shows its
// query text with a Send. The 071 is human-pace: past the daily cap a WhatsApp chase is refused until tomorrow.
function ChasePanel({ c, cap, name, sendsEnabled, onSent }) {
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  async function send(fingerprint, disputeKey) {
    if (inFlight.current || !name.trim()) return
    inFlight.current = true
    setBusy(true)
    const r = await sendJson(`/supplier_orders/${c.order_id}/chase`, 'POST', { name, fingerprint, dispute_key: disputeKey || undefined })
    setBusy(false)
    inFlight.current = false
    if (r.ok) toast.success(r.data.note || 'Saved')
    else toast.error(r.data.error || 'Could not send')
    if (onSent) onSent()
  }
  const disputes = c.disputes || []
  if (!c.needs_chase && disputes.length === 0 && (c.messages || []).length === 0) return null
  const m = c.current
  const resend = !!(m && m.status === 'held' && sendsEnabled)
  return (
    <div className="border-t border-brand-border bg-slate-50 px-4 py-3 text-sm" data-testid="chase-panel">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Chase the supplier</p>
      {c.needs_chase && (
        <>
          <p className="mt-1 text-xs text-brand-ink">{(c.reasons || []).join(' · ')}</p>
          {c.blocked_text ? <p className="mt-1 text-sm text-brand-ink">{c.blocked_text}</p> : (
            <>
              <p className="mt-1 text-xs text-brand-muted">By {c.channel === 'whatsapp' ? 'WhatsApp from the 071' : 'email'} to {c.to_address}
                {c.channel === 'whatsapp' && cap ? ` · 071 supplier WhatsApps today: ${cap.used_today} of ${cap.cap}` : ''}</p>
              <pre className="mt-1 whitespace-pre-wrap break-words rounded-xl border border-brand-border bg-white p-3 font-sans text-sm text-brand-ink">{c.body}</pre>
              {m && <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">{statusText(m, sendsEnabled)}</p>}
              <button type="button" onClick={() => send(c.fingerprint)} disabled={!c.can_send || busy || !name.trim() || (m && !resend)}
                className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
                <Send size={14} /> {busy ? 'Sending…' : (m ? (resend ? 'Send now' : 'Saved') : 'Send chase')}
              </button>
            </>
          )}
        </>
      )}
      {disputes.map((d) => (
        <div key={d.key} className="mt-3 rounded-xl border border-amber-200 bg-white p-3" data-testid="chase-dispute">
          <p className="text-xs font-semibold text-brand-ink">{{ price_difference: 'Price difference', credit_claim: 'Credit claim', unallocated_charge: 'Charge not on our order' }[d.kind] || d.kind} · {d.item} · R{d.amount}</p>
          <p className="text-xs text-brand-muted">{d.text}</p>
          {d.body ? (
            <>
              <p className="mt-1 text-xs text-emerald-800">Paul approved raising this{d.decision && d.decision.by ? ` (${d.decision.by})` : ''}.</p>
              <pre className="mt-1 whitespace-pre-wrap break-words rounded-xl border border-brand-border bg-slate-50 p-2 font-sans text-xs text-brand-ink">{d.body}</pre>
              {d.current && <p className="mt-1 text-xs text-amber-900">{statusText(d.current, sendsEnabled)}</p>}
              <button type="button" onClick={() => send(d.fingerprint, d.key)} disabled={!d.can_send || busy || !name.trim() || (d.current && !(d.current.status === 'held' && sendsEnabled))}
                className="mt-1 inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
                <Send size={12} /> Send the query
              </button>
            </>
          ) : <p className="mt-1 text-xs font-medium text-amber-900">{d.note}</p>}
        </div>
      ))}
      {!name.trim() && <p className="mt-1 text-xs text-brand-muted">Type your name above first.</p>}
    </div>
  )
}

function statusText(m, sendsEnabled) {
  if (!m) return null
  if (m.status === 'held') return sendsEnabled ? `Saved, not sent${m.error ? `: ${m.error}` : ''}. Tap Send now to send it.` : 'Sends are switched off: saved, not sent'
  if (m.status === 'queued') return `Queued - waiting for the 071 sender since ${when(m.updated_at || m.requested_at)}`
  if (m.status === 'sending') return m.stuck ? `Stuck since ${when(m.claimed_at)}: check the phone, then mark it above` : `Handed over at ${when(m.claimed_at)}: state unknown until the sender reports`
  if (m.status === 'sent') return `Sent ${when(m.sent_at)} to ${m.to_address}`
  if (m.status === 'failed') return `Failed: ${m.error || 'no reason given'}`
  if (m.status === 'cancelled') return `Withdrawn: ${m.error || ''}`
  return m.status
}

function EnquiryPanel({ e, name, sendsEnabled, onSent }) {
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const m = e.current
  const resend = !!(m && m.status === 'held' && sendsEnabled)
  const canTap = e.can_send && (!m || resend)
  async function send() {
    if (inFlight.current || !name.trim()) return
    inFlight.current = true
    setBusy(true)
    // The fingerprint of the text + address shown here: the server refuses the tap if either changed meanwhile.
    const r = await sendJson(`/supplier_orders/${e.order_id}/enquiry`, 'POST', { name, fingerprint: e.fingerprint })
    setBusy(false)
    inFlight.current = false
    if (r.ok) toast.success(r.data.note || 'Saved')
    else toast.error(r.data.error || 'Could not send')
    if (onSent) onSent()
  }
  return (
    <div className="border-t border-brand-border bg-slate-50 px-4 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Ask for specials</p>
      {e.blocked_text ? (
        <p className="mt-1 text-sm text-brand-ink">{e.blocked_text}</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-brand-muted">Price enquiry by {e.channel === 'whatsapp' ? 'WhatsApp from the 071' : 'email'} to {e.to_address}</p>
          <pre className="mt-1 whitespace-pre-wrap break-words rounded-xl border border-brand-border bg-white p-3 font-sans text-sm text-brand-ink">{e.body}</pre>
          {m && (
            <p className={cn('mt-2 rounded-xl px-3 py-2 text-xs font-medium', m.status === 'sent' ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900')}>
              {statusText(m, sendsEnabled)}
            </p>
          )}
          <button type="button" onClick={send} disabled={!canTap || busy || !name.trim()}
            className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
            <Send size={14} /> {busy ? 'Sending…' : (m ? (resend ? 'Send now' : 'Saved') : 'Send')}
          </button>
          {!name.trim() && <p className="mt-1 text-xs text-brand-muted">Type your name above first.</p>}
        </>
      )}
    </div>
  )
}

function StuckList({ rows, name, onDone }) {
  return (
    <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-red-900"><Clock size={16} /> Stuck supplier messages ({rows.length})</p>
      <p className="text-xs text-red-900">The 071 sender took these but never said whether they went. They are never sent again by themselves: check the phone, then mark each one.</p>
      {rows.map((m) => <StuckRow key={m.id} m={m} name={name} onDone={onDone} />)}
    </div>
  )
}

function StuckRow({ m, name, onDone }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  async function mark(outcome) {
    if (busy || !note.trim() || !name.trim()) return
    setBusy(true)
    const r = await sendJson(`/supplier_messages/${m.id}/resolve`, 'PATCH', { outcome, note: note.trim(), name })
    setBusy(false)
    if (r.ok) toast.success(`Marked ${outcome}`)
    else toast.error(r.data.error || 'Could not save')
    if (onDone) onDone()
  }
  return (
    <div className="mt-2 rounded-xl border border-red-200 bg-white p-2 text-sm">
      <p className="text-xs text-brand-muted">{m.kind} to {m.to_address} · taken by the sender at {when(m.claimed_at)}</p>
      <input type="text" value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="What happened (required)" className={cn(INPUT, 'mt-1')} />
      <div className="mt-1 flex gap-2">
        <button type="button" onClick={() => mark('sent')} disabled={busy || !note.trim() || !name.trim()} className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">It went: mark sent</button>
        <button type="button" onClick={() => mark('failed')} disabled={busy || !note.trim() || !name.trim()} className="rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">It did not go: mark failed</button>
      </div>
    </div>
  )
}

function LineRow({ line, editable, name, special, onSpecialChanged }) {
  const [reason, setReason] = useState(line.keep_reason || '')
  const [saving, setSaving] = useState(false)
  const counter = line.detail && line.detail.counter
  const openReturn = line.flags.includes('open_return')
  async function saveReason() {
    if (!reason.trim() || saving) return
    setSaving(true)
    const r = await sendJson(`/supplier_order_lines/${line.id}/keep_reason`, 'PATCH', { keep_reason: reason.trim(), name })
    setSaving(false)
    if (r.ok) toast.success('Reason saved')
    else toast.error(r.data.error || 'Could not save the reason')
  }
  return (
    <div className="px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">{line.name}</p>
          <p className="text-xs text-brand-muted">Code {line.supplier_code || '-'} · need {line.pieces_needed} pieces</p>
          <p className="mt-0.5 text-sm text-brand-ink">{packText(line)} · {rands(line.unit_price)} per box</p>
          {line.months_of_cover != null && <p className="text-xs text-brand-muted">{line.months_of_cover} months of cover</p>}
        </div>
        <p className="shrink-0 font-semibold text-brand-ink">{rands(line.line_total)}</p>
      </div>
      {line.flags.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {line.flags.map((f) => (
            <span key={f} className={cn('rounded-full px-2 py-0.5 text-xs font-medium', WARN_FLAGS.has(f) ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700')}>
              {FLAG_LABELS[f] || f}{f === 'counter_changed' && counter ? `: ${counter.to_order} (par says ${counter.par_minus_counted})` : ''}
            </span>
          ))}
        </div>
      )}
      <SpecialLine d={special} name={name} onChanged={onSpecialChanged} />
      {openReturn && (
        <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-2">
          <p className="flex items-center gap-1 text-xs font-semibold text-amber-900"><AlertTriangle size={14} /> This item has an open return. Keep it on the order?</p>
          <div className="mt-1 flex flex-col gap-1.5 sm:flex-row">
            <input type="text" value={reason} disabled={!editable} onChange={(e) => setReason(e.target.value)} placeholder="Keep, because ..." className={INPUT} />
            <button type="button" onClick={saveReason} disabled={!editable || saving || !reason.trim() || !name.trim()}
              className="shrink-0 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
              {saving ? 'Saving…' : 'Save reason'}
            </button>
          </div>
          {!name.trim() && <p className="mt-1 text-xs text-brand-muted">Type your name above first.</p>}
        </div>
      )}
    </div>
  )
}

function NeedsCheckingRow({ row, suppliers, name }) {
  const it = row.item || {}
  const [open, setOpen] = useState(false)
  const [f, setF] = useState({ supplier_id: it.supplier_id || '', supplier_code: it.supplier_code || '', pack_size: it.pack_size || '', last_price: it.last_price || '' })
  const [saving, setSaving] = useState(false)
  const photo = (it.photos || [])[0]
  const set = (k) => (e) => setF((cur) => ({ ...cur, [k]: e.target.value }))
  async function save() {
    if (saving) return
    setSaving(true)
    const r = await sendJson(`/stock_items/${row.stock_item_id}/verification`, 'PATCH', { ...f, entry_id: row.entry_id, name })
    setSaving(false)
    if (!r.ok) { toast.error(r.data.error || 'Could not save'); return }
    const rp = r.data.replan
    if (rp && rp.ok === false) toast.error(`Checked, but the order did not change: ${rp.error}`)
    else toast.success(rp && rp.po_number ? `Checked: on ${rp.po_number}` : 'Checked')
    router.reload({ preserveScroll: true })
  }
  return (
    <div className="px-4 py-3 text-sm">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 text-left">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">{row.name}</p>
          <p className="text-xs text-brand-muted">Missing: {(row.missing || []).join(', ')} · {row.pieces_needed} pieces</p>
        </div>
        <span className="shrink-0 rounded-lg border border-brand-primary px-2 py-1 text-xs font-semibold text-brand-primary">{open ? 'Close' : 'Check'}</span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2">
          {photo && photo.url && <img src={photo.url} alt={`shelf photo of ${row.name}`} className="max-h-48 w-auto rounded-xl border border-brand-border object-contain" />}
          <label className="text-xs text-brand-muted">Supplier
            <select value={f.supplier_id} onChange={set('supplier_id')} className={cn(INPUT, 'mt-1')}>
              <option value="">Pick the supplier</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.excluded ? ' (excluded)' : ''}</option>)}
            </select>
          </label>
          <label className="text-xs text-brand-muted">Supplier code (from the box)
            <input type="text" value={f.supplier_code} onChange={set('supplier_code')} className={cn(INPUT, 'mt-1')} />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-brand-muted">Pieces per box
              <input inputMode="numeric" value={f.pack_size} onChange={set('pack_size')} className={cn(INPUT, 'mt-1')} />
            </label>
            <label className="text-xs text-brand-muted">Last price per box (R, ex VAT)
              <input inputMode="decimal" value={f.last_price} onChange={set('last_price')} className={cn(INPUT, 'mt-1')} />
            </label>
          </div>
          <button type="button" onClick={save} disabled={saving || !name.trim() || !f.supplier_id}
            className="rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
            {saving ? 'Saving…' : 'Confirm from the box'}
          </button>
          {!name.trim() && <p className="text-xs text-brand-muted">Type your name above first.</p>}
        </div>
      )}
    </div>
  )
}

function ListSection({ icon, title, note, children }) {
  const items = React.Children.toArray(children)
  return (
    <div className="mt-6">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink">{icon} {title}</h2>
      {note && <p className="text-xs text-brand-muted">{note}</p>}
      <div className="mt-2 divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">
        {items.length ? items : <p className="px-4 py-3 text-sm text-brand-muted">None.</p>}
      </div>
    </div>
  )
}

// ── SPECIALS 2 of 2 (ordernow1021b2): the wait, "Record the reply", the decision per line, override ────────────────
const CAP_NOTE = 'The R5,000 cap cannot be overridden'
const EMPTY_SPECIAL = { description: '', deal: '', packs: '', price: '', available: '', valid_until: '', expiry: '' }

async function sendForm(url, fd) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'same-origin',
    body: fd,
  })
  let data = {}
  try { data = await res.json() } catch (_) { data = {} }
  return { ok: res.ok && data.ok !== false, data }
}

// Same tablet-side downscale as the count screen's item photos (~1000 px JPEG): the container has no image tools.
async function downscalePhoto(file, maxPx = 1000, quality = 0.75) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      canvas.toBlob((b) => resolve(b && b.size < file.size ? b : file), 'image/jpeg', quality)
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) }
    img.src = url
  })
}

function SpecialLine({ d, name, onChanged }) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  if (!d) return null
  const hasOffer = !!d.offer
  async function override(withdraw) {
    if (busy || !name.trim() || (!withdraw && !reason.trim())) return
    setBusy(true)
    const r = await sendJson(`/supplier_order_lines/${d.id}/special_override`, 'PATCH', { reason: reason.trim(), withdraw, name })
    setBusy(false)
    if (r.ok) { toast.success(withdraw ? 'Override withdrawn' : 'Override saved: the approver will see it'); setReason('') }
    else toast.error(r.data.error || 'Could not save')
    if (onChanged) onChanged()
  }
  return (
    <div className="mt-1.5 text-xs">
      {d.quoted_price != null && <p className="text-brand-muted">Quoted {rands(d.quoted_price)} per box (last price {rands(d.last_price)})</p>}
      {d.no_specials && !hasOffer && <p className="text-brand-muted">No specials on this item</p>}
      {hasOffer && d.special_result && (
        <div className={cn('mt-1 rounded-xl px-2 py-1.5', d.special_result === 'taken' ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900')}>
          <p className="font-semibold">{d.special_result === 'taken' ? 'Special taken' : 'Special declined'}: {d.special_reason}</p>
          {d.special_note && <p>{d.special_note}</p>}
        </div>
      )}
      {hasOffer && !d.special_result && d.special_reason && <p className="mt-1 text-amber-900">{d.special_reason}</p>}
      {d.override_reason && (
        <p className="mt-1 text-amber-900">Override: {d.override_reason}
          <button type="button" onClick={() => override(true)} disabled={busy || !name.trim()} className="ml-2 underline">withdraw</button>
        </p>
      )}
      {hasOffer && d.special_result === 'declined' && !d.override_reason && (d.cap_only ? (
        <p className="mt-1 text-brand-muted">{CAP_NOTE}.</p>
      ) : (
        <div className="mt-1 flex flex-col gap-1.5 sm:flex-row">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Take it anyway, because ..." className={INPUT} />
          <button type="button" onClick={() => override(false)} disabled={busy || !reason.trim() || !name.trim()}
            className="shrink-0 rounded-xl border border-brand-primary px-3 py-2 text-xs font-semibold text-brand-primary disabled:border-slate-200 disabled:text-brand-muted">
            {busy ? 'Saving…' : 'Override with reason'}
          </button>
        </div>
      ))}
    </div>
  )
}

function SpecialsPanel({ r, name, onChanged }) {
  const [skip, setSkip] = useState('')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const w = r.wait || {}
  async function skipWait() {
    if (busy || !skip.trim() || !name.trim()) return
    setBusy(true)
    const res = await sendJson(`/supplier_orders/${r.order_id}/skip_wait`, 'POST', { reason: skip.trim(), name })
    setBusy(false)
    if (res.ok) { toast.success('Wait skipped'); setSkip('') } else toast.error(res.data.error || 'Could not save')
    if (onChanged) onChanged()
  }
  // Re-check specials (fix C5): runs the specials decision again for this order (a re-plan already does it by itself).
  async function recheck() {
    if (busy || !name.trim()) return
    setBusy(true)
    const res = await sendJson(`/supplier_orders/${r.order_id}/specials`, 'POST', { name })
    setBusy(false)
    if (res.ok) toast.success('Specials checked again'); else toast.error(res.data.error || 'Could not check')
    if (onChanged) onChanged()
  }
  const undecided = (r.lines || []).some((l) => l.offer && !l.special_result)
  return (
    <div className="border-t border-brand-border bg-white px-4 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Supplier reply and specials</p>
      <p className={cn('mt-1 rounded-xl px-3 py-2 text-xs font-medium', w.over ? 'bg-slate-100 text-slate-800' : 'bg-amber-50 text-amber-900')}>
        <Clock size={12} className="mr-1 inline" />{w.text || ''}
      </p>
      {!w.over && r.can_record && (
        <div className="mt-2 flex flex-col gap-1.5 sm:flex-row">
          <input type="text" value={skip} onChange={(e) => setSkip(e.target.value)} placeholder="Why we are not waiting (required)" className={INPUT} />
          <button type="button" onClick={skipWait} disabled={busy || !skip.trim() || !name.trim()}
            className="shrink-0 rounded-xl border border-brand-primary px-3 py-2 text-xs font-semibold text-brand-primary disabled:border-slate-200 disabled:text-brand-muted">Skip the wait</button>
        </div>
      )}
      {(r.replies || []).map((rep) => (
        <div key={rep.id} className="mt-2 rounded-xl border border-brand-border p-2 text-xs">
          <p className="text-brand-muted">Reply recorded {when(rep.received_at)} by {rep.recorded_by}{rep.stock_note ? ` · ${rep.stock_note}` : ''}</p>
          {rep.raw_text && <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-brand-ink">{rep.raw_text}</pre>}
          {(rep.photos || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {rep.photos.map((p) => <a key={p.id} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="supplier reply" className="h-16 w-16 rounded-lg border border-brand-border object-cover" /></a>)}
            </div>
          )}
        </div>
      ))}
      {r.can_record && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => setOpen(!open)} className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white">
            {open ? 'Close' : 'Record the reply'}
          </button>
          {(r.replies || []).length > 0 && (
            <button type="button" onClick={recheck} disabled={busy || !name.trim()}
              className={cn('rounded-xl border px-4 py-2 text-sm font-semibold disabled:border-slate-200 disabled:text-brand-muted', undecided ? 'border-amber-500 text-amber-900' : 'border-brand-primary text-brand-primary')}>
              Re-check specials
            </button>
          )}
        </div>
      )}
      {open && <ReplyForm r={r} name={name} onDone={() => { setOpen(false); if (onChanged) onChanged() }} />}
    </div>
  )
}

function ReplyForm({ r, name, onDone }) {
  const [raw, setRaw] = useState('')
  const [note, setNote] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [rows, setRows] = useState(() => Object.fromEntries((r.lines || []).map((l) => [l.stock_item_id, { price: '', no_specials: false, has: false, sp: { ...EMPTY_SPECIAL } }])))
  const setRow = (id, patch) => setRows((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }))
  const setSp = (id, k) => (e) => setRows((cur) => ({ ...cur, [id]: { ...cur[id], sp: { ...cur[id].sp, [k]: e.target.value } } }))
  async function save() {
    if (busy || !name.trim()) return
    const lines = []
    Object.entries(rows).forEach(([id, x]) => {
      const row = { stock_item_id: Number(id) }
      if (x.price.trim()) row.price = x.price.trim()
      if (x.no_specials) row.no_specials = true
      if (x.has) row.special = x.sp
      if (row.price || row.no_specials || row.special) lines.push(row)
    })
    if (!raw.trim() && !files.length && !lines.length) { toast.error('Paste or photograph the reply, or fill in a line'); return }
    setBusy(true)
    const fd = new FormData()
    fd.append('name', name)
    fd.append('raw_text', raw)
    fd.append('stock_note', note)
    fd.append('lines', JSON.stringify(lines))
    for (const f of files) {
      const blob = await downscalePhoto(f)
      fd.append('photos[]', blob, 'reply.jpg')
    }
    const res = await sendForm(`/supplier_orders/${r.order_id}/replies`, fd)
    setBusy(false)
    if (res.ok) { toast.success(res.data.already ? (res.data.note || 'Already recorded') : 'Reply recorded'); if (onDone) onDone() } else toast.error(res.data.error || 'Could not save the reply')
  }
  return (
    <div className="mt-2 grid gap-2 rounded-xl border border-brand-border bg-slate-50 p-3">
      <label className="text-xs text-brand-muted">The reply as received (paste it)
        <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4} className={cn(INPUT, 'mt-1')} />
      </label>
      <label className="text-xs text-brand-muted">Photos of the reply
        <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 6))} className="mt-1 block w-full text-sm" />
      </label>
      <label className="text-xs text-brand-muted">In stock / lead time
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={cn(INPUT, 'mt-1')} />
      </label>
      {(r.lines || []).map((l) => {
        const x = rows[l.stock_item_id]
        if (!x) return null
        return (
          <div key={l.id} className="rounded-xl border border-brand-border bg-white p-2">
            <p className="text-sm font-medium text-brand-ink">{l.name}</p>
            <p className="text-xs text-brand-muted">{l.normal_packs} boxes now at {rands(l.normal_price)} per box</p>
            <label className="mt-1 block text-xs text-brand-muted">Price per box now (R, ex VAT)
              <input inputMode="decimal" value={x.price} onChange={(e) => setRow(l.stock_item_id, { price: e.target.value })} className={cn(INPUT, 'mt-1')} />
            </label>
            <div className="mt-1 flex flex-wrap gap-3 text-xs">
              <label className="flex items-center gap-1"><input type="checkbox" checked={x.no_specials} onChange={(e) => setRow(l.stock_item_id, { no_specials: e.target.checked, has: e.target.checked ? false : x.has })} /> No specials</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={x.has} onChange={(e) => setRow(l.stock_item_id, { has: e.target.checked, no_specials: e.target.checked ? false : x.no_specials })} /> A special</label>
            </div>
            {x.has && (
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <label className="col-span-2 text-xs text-brand-muted">What it is<input type="text" value={x.sp.description} onChange={setSp(l.stock_item_id, 'description')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="col-span-2 text-xs text-brand-muted">The deal<input type="text" value={x.sp.deal} onChange={setSp(l.stock_item_id, 'deal')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="text-xs text-brand-muted">Special boxes<input inputMode="numeric" value={x.sp.packs} onChange={setSp(l.stock_item_id, 'packs')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="text-xs text-brand-muted">Special price per box (R)<input inputMode="decimal" value={x.sp.price} onChange={setSp(l.stock_item_id, 'price')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="text-xs text-brand-muted">Boxes available<input inputMode="numeric" value={x.sp.available} onChange={setSp(l.stock_item_id, 'available')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="text-xs text-brand-muted">Valid until<input type="date" value={x.sp.valid_until} onChange={setSp(l.stock_item_id, 'valid_until')} className={cn(INPUT, 'mt-1')} /></label>
                <label className="col-span-2 text-xs text-brand-muted">Stock expiry date<input type="date" value={x.sp.expiry} onChange={setSp(l.stock_item_id, 'expiry')} className={cn(INPUT, 'mt-1')} /></label>
              </div>
            )}
          </div>
        )
      })}
      <button type="button" onClick={save} disabled={busy || !name.trim()}
        className="rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
        {busy ? 'Saving…' : 'Save the reply'}
      </button>
      {!name.trim() && <p className="text-xs text-brand-muted">Type your name above first.</p>}
    </div>
  )
}

// ── APPROVE + ORDER (ordernow1021c) ─────────────────────────────────────────────────────────────────────────────
// The owner PIN is typed at EVERY approval (the owner unlock window does not count), sent as `approval_secret`, never
// kept: the field is cleared after every tap. Above R10,000 a second person (not the builder / last editor) approves.
function orderStatusText(m, sendsEnabled) {
  if (!m) return null
  if (m.status === 'held') return sendsEnabled ? `Order saved, not sent${m.error ? `: ${m.error}` : ''}. Tap Send now to send it.` : 'Sends are switched off: Order saved, not sent'
  return statusText(m, sendsEnabled)
}

function ApprovalPanel({ a, name, sendsEnabled, onChanged }) {
  const [pin, setPin] = useState('')
  const [approver, setApprover] = useState(name || '')
  const [rejectReason, setRejectReason] = useState('')
  const [voidReason, setVoidReason] = useState('')
  const [correction, setCorrection] = useState('')
  const [number, setNumber] = useState('')
  const [delivery, setDelivery] = useState('')
  const [portalNumber, setPortalNumber] = useState('')
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  useEffect(() => { if (!approver && name) setApprover(name) }, [name]) // eslint-disable-line react-hooks/exhaustive-deps

  async function act(path, body, okText) {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    const r = await sendJson(`/supplier_orders/${a.order_id}${path}`, path === '/placement' ? 'PATCH' : 'POST', body)
    setBusy(false)
    inFlight.current = false
    if (r.ok) toast.success(r.data.note || okText || 'Saved')
    else toast.error(r.data.error || 'Could not save')
    if (onChanged) onChanged()
    return r.ok
  }
  async function approve() {
    const typed = pin
    setPin('')
    await act('/approval', { name: approver, approval_secret: typed }, 'Approved')
  }
  const om = a.order_message
  // Only the messages of the CURRENT approval: a failed / cancelled message of an earlier version never blocks Send.
  const curMsgs = (a.messages || []).filter((m) => m.approval_version === a.approval_version)
  const orderMsg = curMsgs.filter((m) => m.kind === 'order').slice(-1)[0]
  const copyMsg = curMsgs.filter((m) => m.kind === 'order_copy').slice(-1)[0]
  const resend = !!(orderMsg && orderMsg.status === 'held' && sendsEnabled)
  const canSend = !!(om && a.status === 'approved' && (!orderMsg || orderMsg.status === 'cancelled' || resend))
  return (
    <div className="border-t border-brand-border bg-white px-4 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Approve, then order</p>
      <p className="mt-1 text-xs text-brand-muted">
        {a.status.replace(/_/g, ' ')} · total {rands(a.total_ex_vat)} ex VAT · built by {a.built_by || '-'}
        {a.last_edited_by ? ` · last edited by ${a.last_edited_by}` : ''} · {a.wait_text}
        {a.approved_by ? ` · approved by ${a.approved_by} at ${when(a.approved_at)} (version ${a.approval_version})` : ''}
      </p>
      <div className="mt-2 divide-y divide-brand-border rounded-xl border border-brand-border">
        {(a.lines || []).map((l) => (
          <div key={l.id} className="px-3 py-2 text-xs">
            <p className="font-medium text-brand-ink">{l.supplier_code || 'no code'} · {l.name}</p>
            <p className="text-brand-muted">{l.packs} {l.packs === 1 ? 'box' : 'boxes'} of {l.pack_size} = {l.pieces} · {rands(l.unit_price)} per box · {rands(l.line_total)}</p>
            {l.special_result && <p className={l.special_taken ? 'text-emerald-800' : 'text-brand-muted'}>{l.special_taken ? 'Special taken' : 'Special declined'}{l.special_reason ? `: ${l.special_reason}` : ''}{l.special_note ? ` (${l.special_note})` : ''}</p>}
            {l.override_reason && <p className="text-amber-800">Staff override: {l.override_reason}</p>}
            {l.flags.length > 0 && <p className="text-amber-800">{l.flags.map((f) => FLAG_LABELS[f] || f).join(' · ')}</p>}
            {l.flags.includes('open_return') && <p className="text-amber-800">Keep reason: {l.keep_reason || 'MISSING'}</p>}
          </div>
        ))}
      </div>
      {a.wait_skipped_reason && <p className="mt-1 text-xs text-amber-800">Wait skipped: {a.wait_skipped_reason}</p>}
      {a.second_person && !a.locked && (
        <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">Second person needed: over R10,000, so {a.built_by}{a.last_edited_by && a.last_edited_by !== a.built_by ? ` and ${a.last_edited_by}` : ''} may not approve this order.</p>
      )}
      {(a.blockers || []).length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-red-800">{a.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
      )}

      {a.can_approve && (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <label className="text-xs font-medium text-brand-muted">Approver name
            <input type="text" value={approver} onChange={(e) => setApprover(e.target.value)} className={cn(INPUT, 'mt-1')} />
          </label>
          <label className="text-xs font-medium text-brand-muted">Owner PIN
            <input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} className={cn(INPUT, 'mt-1')} />
          </label>
          <button type="button" onClick={approve} disabled={busy || !pin || !approver.trim() || (a.blockers || []).length > 0}
            className="self-end rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">Approve this order</button>
        </div>
      )}
      {a.can_reject && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input type="text" placeholder="Reject needs a reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className={cn(INPUT, 'sm:max-w-sm')} />
          <button type="button" disabled={busy || !rejectReason.trim() || !name.trim()} onClick={() => act('/reject', { name, reason: rejectReason }, 'Rejected')}
            className="rounded-xl border border-red-300 px-3 py-2 text-xs font-semibold text-red-800 disabled:opacity-50">Reject</button>
        </div>
      )}

      {a.status === 'approved' && om && !a.order_left && (
        <div className="mt-3">
          <p className="text-xs text-brand-muted">Order by {om.channel === 'whatsapp' ? 'WhatsApp from the 071' : 'email'} to {om.to_address}{om.copy_to ? ` (email copy to ${om.copy_to}: COPY - not a second order)` : ''}</p>
          <pre className="mt-1 whitespace-pre-wrap break-words rounded-xl border border-brand-border bg-slate-50 p-3 font-sans text-sm text-brand-ink">{om.body}</pre>
          {orderMsg && <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">{orderStatusText(orderMsg, sendsEnabled)}</p>}
          {copyMsg && <p className="mt-1 text-xs text-brand-muted">Email copy: {orderStatusText(copyMsg, sendsEnabled)}</p>}
          <button type="button" disabled={!canSend || busy || !name.trim()} onClick={() => act('/order_message', { name, fingerprint: om.fingerprint }, 'Saved')}
            className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
            <Send size={14} /> {orderMsg && !resend && orderMsg.status !== 'cancelled' ? 'Saved' : (resend ? 'Send now' : 'Send order')}
          </button>
        </div>
      )}
      {a.status === 'approved' && a.send_blocked_text && !a.portal && <p className="mt-2 text-xs text-red-800">{a.send_blocked_text}</p>}

      {a.portal && a.status === 'approved' && (
        <div className="mt-3 rounded-xl border border-brand-border bg-slate-50 p-3">
          <p className="text-sm font-medium text-brand-ink">Place this on the {a.supplier_name} portal</p>
          <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-xs text-brand-ink">{(a.portal.list || []).join('\n')}</pre>
          <button type="button" disabled={busy || !a.portal.can_place || !name.trim()} onClick={() => act('/placed_on_portal', { name }, 'Placed')}
            className="mt-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">I've placed it</button>
        </div>
      )}
      {a.portal && a.portal.notice && (
        <div className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-medium">{a.portal.notice}</p>
          <div className="mt-1 flex flex-wrap gap-2">
            <input type="text" placeholder="Portal order number" value={portalNumber} onChange={(e) => setPortalNumber(e.target.value)} className={cn(INPUT, 'sm:max-w-xs')} />
            <button type="button" disabled={busy || !portalNumber.trim() || !name.trim()} onClick={() => act('/portal_number', { name, number: portalNumber }, 'Saved')}
              className="rounded-xl border border-brand-border px-3 py-2 text-xs font-semibold">Save number</button>
          </div>
        </div>
      )}

      {a.can_void && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input type="text" placeholder="What changes (optional)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} className={cn(INPUT, 'sm:max-w-sm')} />
          <input type="password" inputMode="numeric" autoComplete="off" placeholder="Owner PIN" value={pin} onChange={(e) => setPin(e.target.value)} className={cn(INPUT, 'sm:max-w-[10rem]')} />
          <button type="button" disabled={busy || !name.trim()} onClick={() => { const typed = pin; setPin(''); act('/void_approval', { name, reason: voidReason, approval_secret: typed }, 'Approval voided') }}
            className="rounded-xl border border-amber-300 px-3 py-2 text-xs font-semibold text-amber-900">Change order (voids the approval; owner PIN)</button>
        </div>
      )}
      {a.order_left && (
        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs">
          <p className="font-medium text-brand-ink">{a.status === 'approved' ? 'Order already sent: changes go to the supplier as a chase (d2) and are recorded as a correction event' : `Ordered ${when(a.ordered_at)} by ${a.ordered_by || '-'} (${a.channel_used || '-'})`}</p>
          {a.status !== 'approved' && <p className="mt-1 text-brand-muted">Order already sent: changes go to the supplier as a chase (d2) and are recorded as a correction event.</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <input type="text" placeholder="Correction" value={correction} onChange={(e) => setCorrection(e.target.value)} className={cn(INPUT, 'sm:max-w-sm')} />
            <button type="button" disabled={busy || !correction.trim() || !name.trim()} onClick={async () => { if (await act('/correction', { name, text: correction }, 'Recorded')) setCorrection('') }}
              className="rounded-xl border border-brand-border px-3 py-2 text-xs font-semibold">Record correction</button>
          </div>
          {a.status !== 'approved' && (
            <div className="mt-2 flex flex-wrap gap-2">
              <input type="text" placeholder="Supplier's order number" value={number} onChange={(e) => setNumber(e.target.value)} className={cn(INPUT, 'sm:max-w-xs')} />
              <input type="date" value={delivery} onChange={(e) => setDelivery(e.target.value)} className={cn(INPUT, 'sm:max-w-[12rem]')} />
              <button type="button" disabled={busy || !number.trim() || !name.trim()} onClick={() => act('/placement', { name, supplier_order_number: number, expected_delivery_on: delivery }, 'Saved')}
                className="rounded-xl border border-brand-border px-3 py-2 text-xs font-semibold">Save the supplier's reply</button>
              {a.supplier_order_number && <p className="w-full text-brand-muted">Supplier order {a.supplier_order_number}{a.expected_delivery_on ? ` · delivery ${a.expected_delivery_on}` : ''}</p>}
            </div>
          )}
        </div>
      )}
      {(a.trail || []).length > 0 && (
        <details className="mt-3 text-xs text-brand-muted">
          <summary>Approval trail ({a.trail.length})</summary>
          {a.trail.map((e, i) => <p key={i}>{when(e.at)} · {e.kind.replace(/_/g, ' ')} · {e.by}{e.detail && (e.detail.reason || e.detail.text) ? ` · ${e.detail.reason || e.detail.text}` : ''}</p>)}
        </details>
      )}
    </div>
  )
}

// ── RECEIVE + INVOICES (ordernow1021d1): deliveries (photo of the note + packs per line), closed short, back-order, an
// approver-only substitute (owner PIN), a return; the 3-way match per line; invoices (matched / proposed / Unmatched)
// and Transpharm portal orders. Nothing is sent from here.
// ordernow1021d3: an AGREED price for a substituted line (approver + owner PIN, posted to the substitute endpoint as
// agreed_price + approval_secret) and CORRECTING a delivery (append-only: packs to take off, a reason, a wrong item lodges
// a return note), shown as "Delivery corrected".
const LINE_STATUS_STYLE = {
  Complete: 'bg-emerald-100 text-emerald-800',
  Short: 'bg-amber-100 text-amber-900',
  'Back-order': 'bg-sky-100 text-sky-900',
  Substituted: 'bg-violet-100 text-violet-900',
  'Price difference': 'bg-red-100 text-red-900',
  'Closed short': 'bg-slate-200 text-slate-800',
  'Over-delivered': 'bg-red-100 text-red-900',
}
const MATCH_FLAG_LABELS = {
  over_delivered: 'more delivered than ordered',
  not_invoiced: 'no invoice yet',
  invoice_packs_differ: 'invoice packs differ',
  invoice_price_differs: 'invoice price differs',
  invoice_total_differs: 'invoice total differs from what was delivered',
  invoice_price_missing: 'invoice line has no price',
  invoice_packs_missing: 'invoice line has no quantity',
  awaiting_delivery: 'invoiced, awaiting delivery',
  substitute_not_received: 'substitute accepted, not received yet',
  portal_differs: 'portal order differs from the approved order',
}

function todayIso() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function ReceivingPanel({ r, name, pinConfigured, reasons, onChanged }) {
  const [packs, setPacks] = useState({})
  const [note, setNote] = useState('')
  const [date, setDate] = useState(todayIso())
  const [photos, setPhotos] = useState([])
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(null)
  const [fixing, setFixing] = useState(null)
  async function saveDelivery() {
    if (busy) return
    const lines = {}
    Object.entries(packs).forEach(([id, v]) => { if (String(v).trim() !== '') lines[id] = String(v).trim() })
    if (!Object.keys(lines).length) { toast.error('Tick the packs received on at least one line'); return }
    if (!photos.length) { toast.error('Photograph the delivery note'); return }
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('name', name); fd.append('note_number', note); fd.append('delivered_on', date)
      fd.append('lines', JSON.stringify(lines))
      for (const f of photos) fd.append('photos[]', await downscalePhoto(f), 'delivery-note.jpg')
      const { ok, data } = await sendForm(`/supplier_orders/${r.order_id}/deliveries`, fd)
      if (!ok) { toast.error(data.error || 'The delivery could not be saved'); return }
      toast.success(data.already ? 'That delivery note is already recorded' : 'Delivery saved')
      setPacks({}); setNote(''); setPhotos([]); onChanged()
    } finally { setBusy(false) }
  }
  async function lineAct(l, path, body, okText) {
    const { ok, data } = await sendJson(`/supplier_order_lines/${l.id}/${path}`, 'POST', { name, ...body })
    if (!ok) { toast.error(data.error || 'Not saved'); return false }
    toast.success(data.note || okText); onChanged(); return true
  }
  return (
    <div className="border-t border-brand-border px-4 py-3">
      {(r.money_issues || []).length > 0 && (
        <p className="mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-900">Money does not reconcile yet: {r.money_issues.join(' · ')}</p>
      )}
      <p className="text-sm font-semibold text-brand-ink">Receiving · {r.status.replace(/_/g, ' ')}{r.supplier_order_number ? ` · supplier order ${r.supplier_order_number}` : ''}</p>
      <div className="mt-2 divide-y divide-brand-border rounded-xl border border-brand-border">
        {r.lines.map((l) => (
          <div key={l.id} className="px-3 py-2 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-brand-ink">{l.name} <span className="text-xs text-brand-muted">{l.supplier_code}</span></p>
              {l.line_status && <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', LINE_STATUS_STYLE[l.line_status] || 'bg-slate-100')}>{l.line_status}</span>}
            </div>
            <p className="text-xs text-brand-muted">Ordered {l.packs} {l.packs === 1 ? 'box' : 'boxes'} of {l.pack_size} = {l.pieces} · received {l.received_pieces} · closed short {l.cancelled_pieces} · to come {l.outstanding_pieces}{l.back_order ? ' · back-order' : ''}</p>
            {l.substitute_text && <p className="text-xs text-violet-900">Substitute accepted by {l.substituted_by}: {l.substitute_text}</p>}
            {l.substituted && <AgreedPrice l={l} name={name} pinConfigured={pinConfigured} lineAct={lineAct} />}
            {l.match_flags.length > 0 && <p className="text-xs font-medium text-red-800">{l.match_flags.map((f) => MATCH_FLAG_LABELS[f] || f).join(' · ')}</p>}
            {r.receivable && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-xs text-brand-muted">Packs received
                  <input type="text" inputMode="decimal" value={packs[l.id] || ''} onChange={(e) => setPacks({ ...packs, [l.id]: e.target.value })}
                    className={cn(INPUT, 'mt-1 w-24')} />
                </label>
                {Number(l.outstanding_pieces) > 0 && (
                  <>
                    <button type="button" className="rounded-lg border border-brand-border px-2 py-1 text-xs" onClick={() => lineAct(l, 'close_short', {}, 'Closed short')}>Closed short</button>
                    <button type="button" className="rounded-lg border border-brand-border px-2 py-1 text-xs" onClick={() => lineAct(l, 'back_order', { on: !l.back_order }, 'Saved')}>{l.back_order ? 'Not a back-order' : 'Back-order (it follows)'}</button>
                  </>
                )}
                <button type="button" className="rounded-lg border border-brand-border px-2 py-1 text-xs" onClick={() => setOpen(open === l.id ? null : l.id)}>Different product received</button>
              </div>
            )}
            {open === l.id && <WrongProduct l={l} name={name} pinConfigured={pinConfigured} reasons={reasons} lineAct={lineAct} onDone={() => setOpen(null)} />}
          </div>
        ))}
      </div>
      {r.receivable && (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <label className="text-xs text-brand-muted">Delivery note number
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className={cn(INPUT, 'mt-1')} />
          </label>
          <label className="text-xs text-brand-muted">Delivery date
            <input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} className={cn(INPUT, 'mt-1')} />
          </label>
          <label className="text-xs text-brand-muted">Photo of the delivery note
            <input type="file" accept="image/*" capture="environment" multiple onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 6))} className="mt-1 block w-full text-xs" />
          </label>
          <button type="button" disabled={busy} onClick={saveDelivery}
            className="rounded-xl bg-brand-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:col-span-3">Save delivery</button>
        </div>
      )}
      {r.deliveries.length > 0 && (
        <div className="mt-3 text-xs text-brand-muted">
          <p className="font-semibold text-brand-ink">Deliveries</p>
          {r.deliveries.map((d) => (
            <div key={d.id} className="mt-1">
              <p>Note {d.note_number} · {d.delivered_on} · by {d.received_by} · {d.lines.filter((x) => !x.correction).map((x) => `${x.packs} packs`).join(', ')}</p>
              {d.lines.filter((x) => x.correction).map((x, i) => (
                <p key={`c${i}`} data-testid="delivery-corrected" className="text-amber-900">
                  Delivery corrected · {(r.lines.find((ln) => ln.id === x.line_id) || {}).name || 'line'} {x.packs} packs · {x.reason} · by {x.corrected_by} · {when(x.corrected_at)}{x.wrong_item ? ` · wrong item, return ${x.stock_return_id} lodged` : ''}
                </p>
              ))}
              <div className="mt-1 flex gap-1">{d.photos.map((p) => <a key={p.id} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="Delivery note" className="h-12 w-12 rounded object-cover" /></a>)}</div>
              {CORRECTABLE.includes(r.status) && (
                <button type="button" className="mt-1 rounded-lg border border-brand-border px-2 py-1 text-xs" onClick={() => setFixing(fixing === d.id ? null : d.id)}>Correct this delivery</button>
              )}
              {fixing === d.id && <CorrectDelivery key={`fix-${d.id}`} r={r} d={d} name={name} reasons={reasons} onRefresh={onChanged} onDone={() => { setFixing(null); onChanged() }} />}
            </div>
          ))}
        </div>
      )}
      {r.invoices.length > 0 && (
        <div className="mt-3 text-xs text-brand-muted">
          <p className="font-semibold text-brand-ink">Invoices</p>
          {r.invoices.map((i) => <InvoiceRow key={i.id} i={i} name={name} onChanged={onChanged} />)}
        </div>
      )}
      {r.portal_orders.length > 0 && (
        <div className="mt-3 text-xs text-brand-muted">
          <p className="font-semibold text-brand-ink">Portal order</p>
          {r.portal_orders.map((p) => <PortalRow key={p.id} p={p} name={name} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  )
}

const CORRECTABLE = ['ordered', 'part_delivered', 'delivered', 'closed']

// d3: the price agreed for a substitute (per box ex VAT). Approver only: the owner PIN at every save, cleared at once.
function AgreedPrice({ l, name, pinConfigured, lineAct }) {
  const [price, setPrice] = useState('')
  const [pin, setPin] = useState('')
  const [open, setOpen] = useState(false)
  async function save() {
    const typed = pin
    setPin('')
    if (await lineAct(l, 'substitute', { agreed_price: price, approval_secret: typed }, 'Agreed price recorded')) { setPrice(''); setOpen(false) }
  }
  return (
    <div className="mt-1 text-xs text-violet-900">
      <p>
        {l.agreed_price ? `Agreed price R${l.agreed_price} per box ex VAT (by ${l.agreed_price_by}, ${when(l.agreed_price_at)})` : `No agreed price: the invoice must show R${l.unit_price} per box (the approved price)`}
        {' '}<button type="button" className="underline" onClick={() => setOpen(!open)}>{l.agreed_price ? 'Change agreed price' : 'Record agreed price'}</button>
      </p>
      {open && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input type="text" inputMode="decimal" placeholder="Agreed price per box ex VAT" value={price} onChange={(e) => setPrice(e.target.value)} className={cn(INPUT, 'w-40')} />
          <input type="password" autoComplete="off" placeholder="Owner PIN" value={pin} onChange={(e) => setPin(e.target.value)} className={cn(INPUT, 'w-32')} />
          <button type="button" disabled={!pinConfigured || !price.trim()} onClick={save} className="rounded-lg bg-violet-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Save agreed price</button>
        </div>
      )}
    </div>
  )
}

// d3 fix: the correction form's idempotency token, made ONCE per form render (a re-tap / retry re-sends it; reopening
// the form makes a new one, so a deliberate second correction is recorded).
function newCorrectionToken() {
  const c = typeof window !== 'undefined' && window.crypto
  if (c && c.getRandomValues) {
    const a = new Uint8Array(16)
    c.getRandomValues(a)
    return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`.slice(0, 40).padEnd(16, '0')
}

// d3: a CORRECTING entry on one delivery (never an edit): packs to take off per line, a reason; a wrong item lodges a
// return note too. The server refuses taking off more than the note recorded (the count never goes below 0).
function CorrectDelivery({ r, d, name, reasons, onDone, onRefresh }) {
  const [token] = useState(() => newCorrectionToken())
  const [notice, setNotice] = useState('')
  const [packs, setPacks] = useState({})
  const [reason, setReason] = useState('')
  const [wrong, setWrong] = useState(false)
  const [retReason, setRetReason] = useState('Wrong item')
  const [busy, setBusy] = useState(false)
  const lineIds = [...new Set(d.lines.filter((x) => !x.correction).map((x) => x.line_id))]
  async function save() {
    if (busy) return
    const lines = {}
    Object.entries(packs).forEach(([id, v]) => { if (String(v).trim() !== '') lines[id] = String(v).trim() })
    if (!Object.keys(lines).length) { toast.error('Type the packs to take off on at least one line'); return }
    if (reason.trim().length < 3) { toast.error('Type the reason for the correction'); return }
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('name', name); fd.append('correction', '1'); fd.append('delivery_id', d.id); fd.append('reason', reason)
      fd.append('lines', JSON.stringify(lines)); fd.append('wrong_item', wrong ? '1' : '0'); fd.append('return_reason', retReason)
      fd.append('correction_token', token)
      const { ok, data } = await sendForm(`/supplier_orders/${r.order_id}/deliveries`, fd)
      if (!ok) { toast.error(data.error || 'The correction could not be saved'); return }
      if (data.already) {
        // a re-tap of THIS form: nothing was added twice. Say so on the form, not just in a toast.
        const text = data.note || 'Already recorded: this correction was saved before (not added twice)'
        setNotice(text); toast.warning(text); if (onRefresh) onRefresh()
        return
      }
      toast.success('Delivery corrected')
      onDone()
    } finally { setBusy(false) }
  }
  return (
    <div data-testid="correct-delivery" className="mt-2 grid gap-2 rounded-xl bg-brand-surface p-3">
      <p className="text-xs font-semibold text-brand-ink">Correct note {d.note_number}: packs to take off (added as a correcting entry, never an edit)</p>
      {notice && (
        <p data-testid="correction-already" className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">
          {notice}. To take off more, close this form and open a new one.
        </p>
      )}
      {lineIds.map((id) => (
        <label key={id} className="text-xs text-brand-muted">{(r.lines.find((ln) => ln.id === id) || {}).name || `line ${id}`}
          <input type="text" inputMode="decimal" value={packs[id] || ''} onChange={(e) => setPacks({ ...packs, [id]: e.target.value })} className={cn(INPUT, 'mt-1 w-24')} />
        </label>
      ))}
      <input type="text" placeholder="Reason (e.g. counted twice, 1 box over-delivered)" value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT} />
      <label className="flex items-center gap-2 text-xs text-brand-ink">
        <input type="checkbox" checked={wrong} onChange={(e) => setWrong(e.target.checked)} /> Wrong item: lodge a return for these packs
      </label>
      {wrong && (
        <select value={retReason} onChange={(e) => setRetReason(e.target.value)} className={INPUT}>
          {(reasons || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      <button type="button" disabled={busy || !!notice} onClick={save} className="rounded-lg bg-amber-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Save correction</button>
    </div>
  )
}

function WrongProduct({ l, name, pinConfigured, reasons, lineAct, onDone }) {
  const [product, setProduct] = useState('')
  const [code, setCode] = useState('')
  const [pin, setPin] = useState('')
  const [qty, setQty] = useState('')
  const [reason, setReason] = useState('Wrong item')
  async function accept() {
    const done = await lineAct(l, 'substitute', { product, code, approval_secret: pin }, 'Substitute accepted')
    setPin('')
    if (done) onDone()
  }
  async function lodge() {
    if (await lineAct(l, 'return', { quantity: qty, reason }, 'Return lodged')) onDone()
  }
  return (
    <div className="mt-2 grid gap-2 rounded-xl bg-brand-surface p-3 sm:grid-cols-2">
      <div>
        <p className="text-xs font-semibold text-brand-ink">Accept substitute (approver only)</p>
        <input type="text" placeholder="Product received" value={product} onChange={(e) => setProduct(e.target.value)} className={cn(INPUT, 'mt-1')} />
        <input type="text" placeholder="Its supplier code (required: on the delivery note or the pack)" value={code} onChange={(e) => setCode(e.target.value)} className={cn(INPUT, 'mt-1')} />
        <input type="password" autoComplete="off" placeholder="Owner PIN" value={pin} onChange={(e) => setPin(e.target.value)} className={cn(INPUT, 'mt-1')} />
        <button type="button" disabled={!pinConfigured} onClick={accept} className="mt-1 rounded-lg bg-violet-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Accept substitute</button>
      </div>
      <div>
        <p className="text-xs font-semibold text-brand-ink">Or lodge a return</p>
        <input type="text" inputMode="decimal" placeholder="Quantity (pieces)" value={qty} onChange={(e) => setQty(e.target.value)} className={cn(INPUT, 'mt-1')} />
        <select value={reason} onChange={(e) => setReason(e.target.value)} className={cn(INPUT, 'mt-1')}>
          {(reasons || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <button type="button" onClick={lodge} className="mt-1 rounded-lg border border-brand-border px-3 py-1 text-xs font-semibold">Lodge a return</button>
      </div>
    </div>
  )
}

function InvoiceRow({ i, name, onChanged, pickable }) {
  const [orderId, setOrderId] = useState('')
  async function act(path, body, okText) {
    const { ok, data } = await sendJson(`/supplier_invoices/${i.id}/${path}`, 'POST', { name, ...body })
    if (!ok) { toast.error(data.error || 'Not saved'); return }
    toast.success(data.note || okText); onChanged()
  }
  const options = (pickable || []).filter((o) => !i.supplier_id || o.supplier_id === i.supplier_id)
  return (
    <div className="mt-1 rounded-lg border border-brand-border px-2 py-1">
      <p className="text-brand-ink">Invoice {i.invoice_number} · {i.supplier_name} · {i.invoice_date} · {rands(i.total_incl_vat)} incl VAT · {i.match_status}
        {i.pdf_url && <> · <a href={i.pdf_url} target="_blank" rel="noreferrer" className="underline">PDF</a></>}</p>
      {i.match_note && <p>{i.match_note}</p>}
      {i.checks.length > 0 && <p className="text-red-800">{i.checks.join(' · ')}</p>}
      {i.match_status === 'proposed' && (
        <div className="mt-1 flex gap-2">
          <button type="button" className="rounded-lg bg-brand-primary px-2 py-1 text-white" onClick={() => act('confirm', { accept: true }, 'Matched')}>Confirm match</button>
          <button type="button" className="rounded-lg border border-brand-border px-2 py-1" onClick={() => act('confirm', { accept: false }, 'Rejected')}>Not this order</button>
        </div>
      )}
      {i.match_status !== 'matched' && pickable && (
        <div className="mt-1 flex gap-2">
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className={cn(INPUT, 'py-1 text-xs')}>
            <option value="">Pick the order…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.po_number} · {o.supplier_name} · {o.status.replace(/_/g, ' ')}</option>)}
          </select>
          <button type="button" disabled={!orderId} className="rounded-lg border border-brand-border px-2 py-1 disabled:opacity-50" onClick={() => act('pick', { order_id: orderId }, 'Matched')}>Match</button>
        </div>
      )}
    </div>
  )
}

function PortalRow({ p, name, onChanged, pickable }) {
  const [orderId, setOrderId] = useState('')
  async function act(path, body, okText) {
    const { ok, data } = await sendJson(`/supplier_portal_orders/${p.id}/${path}`, 'POST', { name, ...body })
    if (!ok) { toast.error(data.error || 'Not saved'); return }
    toast.success(data.note || okText); onChanged()
  }
  const options = (pickable || []).filter((o) => !p.supplier_id || o.supplier_id === p.supplier_id)
  return (
    <div className="mt-1 rounded-lg border border-brand-border px-2 py-1">
      <p className="text-brand-ink">Portal order {p.portal_order_number} · {p.supplier_name} · {p.status}</p>
      {p.differs.length > 0 && <p className="text-red-800">Differs from the approved order: {p.differs.map((d) => `${d.code}: ${d.issue}`).join(' · ')}</p>}
      {p.status === 'attached' && <button type="button" className="mt-1 rounded-lg bg-brand-primary px-2 py-1 text-white" onClick={() => act('confirm', {}, 'Confirmed')}>Confirm portal order</button>}
      {p.status === 'unattached' && pickable && (
        <div className="mt-1 flex gap-2">
          <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className={cn(INPUT, 'py-1 text-xs')}>
            <option value="">Pick the order…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.po_number} · {o.supplier_name}</option>)}
          </select>
          <button type="button" disabled={!orderId} className="rounded-lg border border-brand-border px-2 py-1 disabled:opacity-50" onClick={() => act('pick', { order_id: orderId }, 'Attached')}>Attach</button>
        </div>
      )}
    </div>
  )
}

const EMPTY_INVOICE = { supplier: '', invoice_number: '', invoice_date: '', total_incl_vat: '', total_ex_vat: '', po_number: '' }

function UnmatchedSection({ takeId, recv, name, onChanged }) {
  const [f, setF] = useState(EMPTY_INVOICE)
  const [busy, setBusy] = useState(false)
  async function typeInvoice() {
    if (busy) return
    setBusy(true)
    try {
      const { ok, data } = await sendJson(`/stock_takes/${takeId}/invoices`, 'POST', { name, ...f })
      if (!ok) { toast.error(data.error || 'The invoice could not be saved'); return }
      toast.success(data.created ? 'Invoice saved' : (data.changed ? 'Invoice updated' : 'Already recorded'))
      setF(EMPTY_INVOICE); onChanged()
    } finally { setBusy(false) }
  }
  const field = (k, label, type = 'text') => (
    <label className="text-xs text-brand-muted">{label}
      <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={cn(INPUT, 'mt-1')} />
    </label>
  )
  return (
    <ListSection icon={<ClipboardCheck size={16} />} title={`Unmatched invoices (${recv.unmatched_invoices.length})`}
      note="Invoices and portal orders that match no order yet: confirm the proposal or pick the order.">
      {recv.unmatched_invoices.map((i) => <div key={i.id} className="px-4 py-2 text-xs text-brand-muted"><InvoiceRow i={i} name={name} onChanged={onChanged} pickable={recv.pickable_orders} /></div>)}
      {recv.unattached_portal_orders.map((p) => <div key={`p${p.id}`} className="px-4 py-2 text-xs text-brand-muted"><PortalRow p={p} name={name} onChanged={onChanged} pickable={recv.pickable_orders} /></div>)}
      <div className="grid gap-2 px-4 py-3 sm:grid-cols-3">
        <p className="text-sm font-semibold text-brand-ink sm:col-span-3">Type an invoice from paper</p>
        {field('supplier', 'Supplier')}
        {field('invoice_number', 'Invoice number')}
        {field('invoice_date', 'Invoice date', 'date')}
        {field('total_incl_vat', 'Total incl VAT (R)')}
        {field('total_ex_vat', 'Total ex VAT (R)')}
        {field('po_number', 'Our PO number (if on it)')}
        <button type="button" disabled={busy} onClick={typeInvoice} className="rounded-xl bg-brand-primary px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:col-span-3">Save invoice</button>
      </div>
    </ListSection>
  )
}
